import type {
  Branding,
  Cents,
  CourseLevel,
  EnrollmentStatus,
  OrderStatus,
  PaymentMethod,
  SaleUnit,
  SeatStatus,
  StoreInfo,
  TaskPriority,
  TaskStatus,
} from '@centerpanos/types';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3333/api';

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly errors: FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  fieldMessage(field: string): string | undefined {
    return this.errors.find((error) => error.field === field)?.message;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  retry?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${API_URL}${path}`, window.location.href);

  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    body:
      options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
  });

  if (response.status === 401 && !options.retry && path !== '/auth/refresh') {
    const renewed = await refreshSession();
    if (renewed) return request<T>(path, { ...options, retry: true });
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string;
      errors?: FieldError[];
    } | null;

    throw new ApiError(
      payload?.message ?? 'Não foi possível concluir a operação.',
      response.status,
      payload?.errors ?? [],
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        accessToken = null;
        return false;
      }

      const data = (await response.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

// ─────────────────────────────── Contratos ─────────────────────────────────

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  permissions: string[];
}

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: AuthUser;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface HealthPayload {
  status: 'ok' | 'degraded';
  database: 'up' | 'down';
  uptime: number;
  timestamp: string;
}

// ──────────────────────────────── Usuários ─────────────────────────────────

export interface AdminUserRow {
  id: string;
  name: string;
  email: string;
  active: boolean;
  lastLoginAt: string | null;
  role: { id: string; name: string };
  createdAt: string;
}

export interface RoleRow {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  _count: { users: number };
}

export interface AuditRow {
  id: string;
  entity: string;
  entityId: string;
  action: string;
  diff: Record<string, { de: unknown; para: unknown }> | null;
  ip: string | null;
  createdAt: string;
  actor: { id: string; name: string } | null;
}

// ──────────────────────────── Configurações ────────────────────────────────

export interface BusinessHourDto {
  weekday: number;
  opensAt: string;
  closesAt: string;
  closed: boolean;
}

export interface SettingsPayload {
  info: StoreInfo | null;
  branding: Branding;
  hours: BusinessHourDto[];
  holidays: Array<{ date: string; label: string }>;
}

export interface PublicSettingsPayload {
  logoPrincipal: string | null;
  whatsapp: string | null;
  address: string | null;
  msgFeriado: string | null;
  feriadoAtivo: boolean;
}

// ──────────────────────────────── Pedidos ──────────────────────────────────

export interface OrderCard {
  code: string;
  status: OrderStatus;
  createdAt: string;
  name: string;
  phone: string;
  deliveryLabel: string;
  itemCount: number;
  subtotal: Cents;
  discount: Cents;
  total: Cents;
  finalValue: Cents | null;
  whatsappClickedAt: string | null;
  atrasado: boolean;
}

export interface OrderItemDto {
  name: string;
  variantName?: string | null;
  unit: SaleUnit;
  packSize?: number | null;
  imageUrl?: string | null;
  qty: number;
  unitPrice: Cents;
  lineTotal: Cents;
}

export interface OrderEventDto {
  id: string;
  type: string;
  payload: Record<string, unknown> | null;
  actor: string | null;
  createdAt: string;
}

export interface OrderDetail extends OrderCard {
  email: string | null;
  deliveryNote: string | null;
  couponCode: string | null;
  notesCustomer: string | null;
  notesInternal: string | null;
  lostReason: string | null;
  closedAt: string | null;
  publicToken: string;
  items: OrderItemDto[];
  timeline: OrderEventDto[];
}

export interface KanbanBoard {
  colunas: Array<{
    key: string;
    label: string;
    statuses: OrderStatus[];
    items: OrderCard[];
  }>;
  atualizadoEm: string;
}

export interface OrdersReport {
  periodo: { de: string; ate: string };
  total: number;
  porStatus: Array<{ status: OrderStatus; quantidade: number; estimado: Cents }>;
  vendido: Cents;
  pedidosFechados: number;
  cliquesWhatsApp: number;
}

export interface DeliveryMethodOption {
  id: string;
  label: string;
  type: string;
  priceLabel: string;
  asksAddress: boolean;
}

// ────────────────────────────────── Cursos ─────────────────────────────────

export interface CursoResumoAdmin {
  id: string;
  title: string;
  slug: string;
  price: Cents;
  active: boolean;
  proximasTurmas: number;
  proximaTurma: string | null;
  totalInscricoes: number;
}

export interface TurmaAdmin {
  id: string;
  startsAt: string;
  endsAt: string;
  dataTexto: string;
  room: string | null;
  instructor: string | null;
  instructorPhone: string | null;
  instructorFeeType: string | null;
  instructorFee: number | null;
  instructorPaidAmount: number | null;
  instructorPaidAt: string | null;
  seatsTotal: number;
  notes: string | null;
  cancelledAt: string | null;
  situacao: SeatStatus;
  inscricoes: Record<'confirmada' | 'pendente' | 'lista_de_espera' | 'cancelada', number>;
}

export interface TurmaDetalheAdmin {
  id: string;
  startsAt: string;
  endsAt: string;
  dataTexto: string;
  room: string | null;
  notes: string | null;
  curso: { id: string; title: string; price: number | string };
  seatsTotal: number;
  cancelada: boolean;
  instructor: string | null;
  instructorPhone: string | null;
  instructorFeeType: string | null;
  instructorFee: number | null;
  instructorPaidAmount: number | null;
  instructorPaidAt: string | null;
  cacheCalculado: number | null;
}

export interface CursoDetalheAdmin {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  description: string | null;
  coverUrl: string | null;
  price: Cents;
  durationMinutes: number | null;
  level: CourseLevel;
  materials: string | null;
  active: boolean;
  sessions: TurmaAdmin[];
}

export interface InscricaoAdmin {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  status: EnrollmentStatus;
  statusLabel: string;
  notes: string | null;
  createdAt: string;
  paidAt: string | null;
  paidAmount: number | null;
  totalAmount: number | null;
  paymentMethod: PaymentMethod | null;
}

// ─────────────────────────────── Agenda ────────────────────────────────────

export interface AgendaEventoAdmin {
  id: string;
  courseId: string;
  courseTitle: string;
  startsAt: string;
  endsAt: string;
  dataTexto: string;
  room: string | null;
  instructor: string | null;
  seatsTotal: number;
  confirmadas: number;
  situacao: SeatStatus;
}

// ─────────────────────────────── Tarefas ───────────────────────────────────

export interface MembroAdmin {
  id: string;
  name: string;
}

export interface TarefaCard {
  id: string;
  title: string;
  description: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  assigneeId: string | null;
  assigneeName: string | null;
  dueDate: string | null;
  atrasada: boolean;
  createdAt: string;
}

export interface TarefaKanban {
  colunas: Array<{
    key: TaskStatus;
    label: string;
    items: TarefaCard[];
  }>;
  atualizadoEm: string;
}

// ─────────────────────────────── API calls ─────────────────────────────────

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => request<AuthUser>('/auth/me'),
  health: () => request<HealthPayload>('/health'),

  settings: {
    getPublic: () => request<PublicSettingsPayload>('/admin/settings/public'),
    get: () => request<SettingsPayload>('/admin/settings'),
    updateStore: (body: unknown) =>
      request<StoreInfo>('/admin/settings/store', { method: 'PUT', body }),
    updateHours: (hours: BusinessHourDto[]) =>
      request<BusinessHourDto[]>('/admin/settings/hours', { method: 'PUT', body: { hours } }),
    addHoliday: (date: string, label: string) =>
      request<{ date: string; label: string }>('/admin/settings/holidays', {
        method: 'POST',
        body: { date, label },
      }),
    removeHoliday: (date: string) =>
      request<{ ok: true }>(`/admin/settings/holidays/${date}`, { method: 'DELETE' }),
    getBranding: () => request<Branding>('/admin/settings/branding'),
    updateBranding: (body: Partial<Branding>) =>
      request<Branding>('/admin/settings/branding', { method: 'PUT', body }),
    uploadBrand: (slot: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return request<Branding>(`/admin/settings/branding/${slot}`, { method: 'POST', formData });
    },
  },

  users: {
    list: () => request<AdminUserRow[]>('/admin/users'),
    create: (body: unknown) => request<AdminUserRow>('/admin/users', { method: 'POST', body }),
    update: (id: string, body: unknown) =>
      request<AdminUserRow>(`/admin/users/${id}`, { method: 'PUT', body }),
    deactivate: (id: string) => request<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' }),
    roles: () => request<RoleRow[]>('/admin/roles'),
    updateRole: (id: string, body: unknown) =>
      request<RoleRow>(`/admin/roles/${id}`, { method: 'PUT', body }),
    audit: (query: Record<string, string | number | undefined>) =>
      request<Paginated<AuditRow>>('/admin/audit', { query }),
  },

  pedidos: {
    lista: (query: Record<string, string | number | undefined>) =>
      request<Paginated<OrderCard>>('/admin/orders', { query }),
    kanban: () => request<KanbanBoard>('/admin/orders/kanban'),
    recuperacao: () => request<{ horas: number; items: OrderCard[] }>('/admin/orders/recuperacao'),
    relatorio: (query: Record<string, string | undefined> = {}) =>
      request<OrdersReport>('/admin/orders/relatorio', { query }),
    metodosEntrega: () => request<DeliveryMethodOption[]>('/admin/orders/delivery-methods'),
    detalhe: (code: string) => request<OrderDetail>(`/admin/orders/${code}`),
    criar: (body: unknown) => request<OrderDetail>('/admin/orders', { method: 'POST', body }),
    excluir: (code: string) => request<{ ok: true }>(`/admin/orders/${code}`, { method: 'DELETE' }),
    mudarStatus: (code: string, body: unknown) =>
      request<OrderCard>(`/admin/orders/${code}/status`, { method: 'PATCH', body }),
    anotar: (code: string, texto: string) =>
      request<OrderDetail>(`/admin/orders/${code}/notes`, { method: 'POST', body: { texto } }),
    atualizarCliente: (code: string, body: unknown) =>
      request<OrderDetail>(`/admin/orders/${code}/customer`, { method: 'PATCH', body }),
  },

  cursos: {
    lista: () => request<CursoResumoAdmin[]>('/admin/courses'),
    obter: (id: string) => request<CursoDetalheAdmin>(`/admin/courses/${id}`),
    criar: (body: unknown) =>
      request<CursoDetalheAdmin>('/admin/courses', { method: 'POST', body }),
    atualizar: (id: string, body: unknown) =>
      request<CursoDetalheAdmin>(`/admin/courses/${id}`, { method: 'PUT', body }),
    remover: (id: string) => request<{ ok: true }>(`/admin/courses/${id}`, { method: 'DELETE' }),
    criarTurma: (cursoId: string, body: unknown) =>
      request<CursoDetalheAdmin>(`/admin/courses/${cursoId}/sessions`, { method: 'POST', body }),
    atualizarTurma: (id: string, body: unknown) =>
      request<CursoDetalheAdmin>(`/admin/courses/sessions/${id}`, { method: 'PUT', body }),
    cancelarTurma: (id: string) =>
      request<CursoDetalheAdmin>(`/admin/courses/sessions/${id}/cancel`, { method: 'POST' }),
    inscricoes: (sessionId: string) =>
      request<{ turma: TurmaDetalheAdmin; inscricoes: InscricaoAdmin[] }>(
        `/admin/courses/sessions/${sessionId}/enrollments`,
      ),
    criarInscricao: (sessionId: string, body: unknown) =>
      request<{ turma: TurmaDetalheAdmin; inscricoes: InscricaoAdmin[] }>(
        `/admin/courses/sessions/${sessionId}/enrollments`,
        { method: 'POST', body },
      ),
    atualizarInscricao: (id: string, body: unknown) =>
      request<{ turma: TurmaDetalheAdmin; inscricoes: InscricaoAdmin[] }>(
        `/admin/courses/enrollments/${id}`,
        { method: 'PUT', body },
      ),
    mudarStatusInscricao: (id: string, body: unknown) =>
      request<unknown>(`/admin/courses/enrollments/${id}`, { method: 'PATCH', body }),
    registrarPagamento: (id: string, body: unknown) =>
      request<unknown>(`/admin/courses/enrollments/${id}/payment`, { method: 'PATCH', body }),
    registrarPagamentoProfessor: (sessionId: string, body: unknown) =>
      request<unknown>(`/admin/courses/sessions/${sessionId}/instructor-payment`, {
        method: 'PATCH',
        body,
      }),
    agenda: () => request<AgendaEventoAdmin[]>('/admin/courses/agenda'),
  },

  relatorios: {
    dados: (relatorio: string, periodo: string) =>
      request<unknown>(`/admin/reports/${relatorio}`, { query: { periodo } }),
    baixarCsv: async (relatorio: string, periodo: string) => {
      const url = new URL(`${API_URL}/admin/reports/${relatorio}`, window.location.href);
      url.searchParams.set('periodo', periodo);
      url.searchParams.set('formato', 'csv');

      const headers: Record<string, string> = {};
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

      const res = await fetch(url, { headers, credentials: 'include' });
      if (!res.ok) throw new Error('Não consegui gerar o arquivo.');

      const blob = await res.blob();
      const nome =
        res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ??
        `relatorio-${relatorio}.csv`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = nome;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },

  tarefas: {
    kanban: () => request<TarefaKanban>('/admin/tasks/kanban'),
    membros: () => request<MembroAdmin[]>('/admin/tasks/membros'),
    criar: (body: unknown) => request<TarefaKanban>('/admin/tasks', { method: 'POST', body }),
    mover: (id: string, status: TaskStatus) =>
      request<TarefaKanban>(`/admin/tasks/${id}/status`, { method: 'PATCH', body: { status } }),
    atualizar: (id: string, body: unknown) =>
      request<TarefaKanban>(`/admin/tasks/${id}`, { method: 'PATCH', body }),
    remover: (id: string) => request<TarefaKanban>(`/admin/tasks/${id}`, { method: 'DELETE' }),
  },
};
